import { formatAccuracyFeet } from '@civilgrid/utils/src/geoItems/unitConversion';
import { getSelectedUtilities, resolveLegacyFieldValue } from '@civilgrid/utils/src/geoItems/utilityMeasurements';
import type { VisibilityState } from '@tanstack/react-table';
import type { GeoItem } from '~/common/types';
import Checkbox from '~/components2/forms/Checkbox';
import type { ExportableColumnDef } from '~/components2/Table/types';

function getFieldValue(item: GeoItem, field: string) {
  return item.fields?.[field]?.value ?? '';
}

const defaultColumnDefs: ExportableColumnDef<GeoItem>[] = [
  {
    id: 'select',
    size: 25,
    exportLabel: 'Select All',
    header: ({ table }) => {
      const allChecked = table.getIsAllRowsSelected();

      return (
        <div className="flex h-full w-full items-center justify-center">
          <Checkbox
            label=""
            checked={allChecked}
            value={allChecked}
            onChange={() => {
              table.toggleAllRowsSelected();
            }}
          />
        </div>
      );
    },
    cell: (cell) => {
      const isChecked = cell.row.getIsSelected();

      return (
        <div className="flex h-full w-full items-center justify-center">
          <Checkbox
            label=""
            checked={isChecked}
            value={isChecked}
            onChange={() => {
              cell.row.toggleSelected();
            }}
          />
        </div>
      );
    },
  },
];

export const potholeColumns: ExportableColumnDef<GeoItem>[] = [
  ...defaultColumnDefs,
  {
    id: 'ph#',
    header: 'PH#',
    accessorFn: (g) => getFieldValue(g, 'PH#'),
  },
  {
    id: 'date',
    header: 'Date',
    accessorFn: (g) => g.createdAt.toLocaleString(),
  },
  {
    id: 'utility',
    header: 'Utility',
    accessorFn: (g) => getSelectedUtilities(g.props).join('\n'),
    cellClassName: 'whitespace-pre-wrap',
  },
  {
    id: 'surfaceType',
    header: 'Surface Type',
    accessorFn: (g) => getFieldValue(g, 'Surface Type'),
  },
  {
    id: 'surfaceThickness',
    header: 'Surface Thickness (in.)',
    accessorFn: (g) => getFieldValue(g, 'Surface Thickness'),
  },
  {
    id: 'baseThickness',
    header: 'Base Thickness (in.)',
    accessorFn: (g) => getFieldValue(g, 'Base Thickness'),
  },
  {
    id: 'holeDepth',
    header: 'Hole Depth (in.)',
    accessorFn: (g) => getFieldValue(g, 'Hole Depth'),
  },
  {
    id: 'depthToTop',
    header: 'Depth to Top (in.)',
    accessorFn: (g) => resolveLegacyFieldValue(g.props, 'Depth to Top', undefined, '\n'),
    cellClassName: 'whitespace-pre-wrap',
  },
  {
    id: 'depthToBottom',
    header: 'Depth to Bottom (in.)',
    accessorFn: (g) => resolveLegacyFieldValue(g.props, 'Depth to Bottom', undefined, '\n'),
    cellClassName: 'whitespace-pre-wrap',
  },
  {
    id: 'size',
    header: 'Size (in.)',
    accessorFn: (g) => resolveLegacyFieldValue(g.props, 'Size', undefined, '\n'),
    cellClassName: 'whitespace-pre-wrap',
  },
  {
    id: 'material',
    header: 'Material',
    accessorFn: (g) => resolveLegacyFieldValue(g.props, 'Material', undefined, '\n'),
    cellClassName: 'whitespace-pre-wrap',
  },
  {
    id: 'soil',
    header: 'Soil',
    accessorFn: (g) => getFieldValue(g, 'Soil'),
  },
  {
    id: 'priority',
    header: 'Priority',
    accessorFn: (g) => getFieldValue(g, 'Priority'),
  },
  {
    id: 'location',
    header: 'Location',
    accessorFn: (g) => getFieldValue(g, 'Location'),
  },
  {
    id: 'notes',
    header: 'Notes',
    accessorFn: (g) => getFieldValue(g, 'Notes'),
  },
  {
    id: 'address',
    header: 'Address',
    accessorFn: (g) => getFieldValue(g, 'Address'),
  },
  {
    id: 'createdBy',
    header: 'Created By',
    accessorFn: (g) => g.createdBy.name ?? '',
  },
  {
    id: 'latitude',
    header: 'Lat',
    accessorFn: (g) => g.geometry.coordinates[1] ?? '',
    cell: (cell) => {
      const v = cell.getValue<number>();
      return v.toFixed(6);
    },
  },
  {
    id: 'longitude',
    header: 'Lng',
    accessorFn: (g) => g.geometry.coordinates[0] ?? '',
    cell: (cell) => {
      const v = cell.getValue<number>();
      return v.toFixed(6);
    },
  },
  {
    // Source values are stored in meters; always shown in feet with the unit spelled
    // out, matching the CSV/KMZ/PDF exports.
    id: 'hrms',
    header: 'HRMS (ft)',
    accessorFn: (g) => (g.accuracy?.hrms != null ? `${formatAccuracyFeet(g.accuracy.hrms)} ft` : ''),
  },
  {
    id: 'vrms',
    header: 'VRMS (ft)',
    accessorFn: (g) => (g.accuracy?.vrms != null ? `${formatAccuracyFeet(g.accuracy.vrms)} ft` : ''),
  },
  {
    id: 'solutionType',
    header: 'Solution Type',
    accessorFn: (g) => g.accuracy?.solutionType ?? '',
  },
  {
    id: 'receiverMake',
    header: 'Receiver Make',
    accessorFn: (g) => g.accuracy?.receiverMake ?? '',
  },
  {
    id: 'receiverModel',
    header: 'Receiver Model',
    accessorFn: (g) => g.accuracy?.receiverModel ?? '',
  },
  {
    id: 'recordedAt',
    header: 'Recorded At (UTC)',
    accessorFn: (g) => g.accuracy?.recordedAt ?? '',
  },
];

export const manholeColumns: ExportableColumnDef<GeoItem>[] = [
  ...defaultColumnDefs,
  {
    id: 'mh#',
    header: 'MH#',
    accessorFn: (g) => getFieldValue(g, 'MH#'),
  },
  {
    id: 'date',
    header: 'Date',
    accessorFn: (g) => g.createdAt.toLocaleString(),
  },
  {
    id: 'siteChoice',
    header: 'Site Choice',
    accessorFn: (g) => getFieldValue(g, 'Site Choice'),
  },
  {
    id: 'crewMembers',
    header: 'Crew Members',
    accessorFn: (g) => getFieldValue(g, 'Crew Members'),
  },
  {
    id: 'weatherConditions',
    header: 'Weather Conditions',
    accessorFn: (g) => getFieldValue(g, 'Weather Conditions'),
  },
  {
    id: 'addressLocation',
    header: 'Address/Location',
    accessorFn: (g) => getFieldValue(g, 'Address/Location'),
  },
  {
    id: 'depth',
    header: 'Depth',
    accessorFn: (g) => getFieldValue(g, 'Depth'),
  },
  {
    id: 'velocity',
    header: 'Velocity',
    accessorFn: (g) => getFieldValue(g, 'Velocity'),
  },
  {
    id: 'heightInf1',
    header: 'Height (Inf1)',
    accessorFn: (g) => getFieldValue(g, 'Height (Inf1)'),
  },
  {
    id: 'widthInf1',
    header: 'Width (Inf1)',
    accessorFn: (g) => getFieldValue(g, 'Width (Inf1)'),
  },
  {
    id: 'materialInf1',
    header: 'Material (Inf1)',
    accessorFn: (g) => getFieldValue(g, 'Material (Inf1)'),
  },
  {
    id: 'shapeInf1',
    header: 'Shape (Inf1)',
    accessorFn: (g) => getFieldValue(g, 'Shape (Inf1)'),
  },
  {
    id: 'shapeEff',
    header: 'Shape (Eff)',
    accessorFn: (g) => getFieldValue(g, 'Shape (Eff)'),
  },
  {
    id: 'sedimentPresent',
    header: 'Sediment Present',
    accessorFn: (g) => getFieldValue(g, 'Sediment Present'),
  },
  {
    id: 'hardPacked',
    header: 'Hard Packed',
    accessorFn: (g) => getFieldValue(g, 'Hard Packed'),
  },
  {
    id: 'softPacked',
    header: 'Soft Packed',
    accessorFn: (g) => getFieldValue(g, 'Soft Packed'),
  },
  {
    id: 'surcharge',
    header: 'Surcharge',
    accessorFn: (g) => getFieldValue(g, 'Surcharge'),
  },
  {
    id: 'distanceFromRim',
    header: 'Distance From Rim',
    // case-sensitive
    accessorFn: (g) => getFieldValue(g, 'Distance from rim'),
  },
  {
    id: 'O2level',
    header: 'O2 Level',
    accessorFn: (g) => getFieldValue(g, 'O2 Level'),
  },
  {
    id: 'siteAccess',
    header: 'Site Access',
    accessorFn: (g) => getFieldValue(g, 'Site Access'),
  },
  {
    id: 'elevatedManhole',
    header: 'Elevated Manhole',
    accessorFn: (g) => getFieldValue(g, 'Elevated Manhole'),
  },
  {
    id: 'boltedManhole',
    header: 'Bolted Manhole',
    accessorFn: (g) => getFieldValue(g, 'Bolted Manhole'),
  },
  {
    id: 'heightAboveGround',
    header: 'Height Above Ground',
    // case-sensitive
    accessorFn: (g) => getFieldValue(g, 'Height above ground'),
  },
  {
    id: 'manholeDepth',
    header: 'Manhole Depth',
    accessorFn: (g) => getFieldValue(g, 'Manhole Depth'),
  },
  {
    id: 'pipeOffset',
    header: 'Pipe Offset',
    // case sensitive
    accessorFn: (g) => getFieldValue(g, 'Pipe offset'),
  },
  {
    id: 'structuralIntegrity',
    header: 'Structural Integrity',
    accessorFn: (g) => getFieldValue(g, 'Structural Integrity'),
  },
  {
    id: 'pipeBends',
    header: 'Pipe Bends',
    accessorFn: (g) => getFieldValue(g, 'Pipe Bends'),
  },
  {
    id: 'distanceToBend',
    header: 'Distance to Bend',
    // case-sensitive
    accessorFn: (g) => getFieldValue(g, 'Distance to bend'),
  },
  {
    id: 'sizeGeomMaterialChange',
    header: 'Size/Geom/Material Change',
    accessorFn: (g) => getFieldValue(g, 'Size/Geom/Material Change'),
  },
  {
    id: 'distanceToChange',
    header: 'Distance to Change',
    accessorFn: (g) => getFieldValue(g, 'Distance to change'),
  },
  {
    id: 'changeDetails',
    header: 'Change Details',
    accessorFn: (g) => getFieldValue(g, 'Change details'),
  },
  {
    id: 'canMaintainSite',
    header: 'Can Maintain Site?',
    accessorFn: (g) => getFieldValue(g, 'Can maintain site?'),
  },
  {
    id: 'siteComments',
    header: 'Site Comments',
    accessorFn: (g) => getFieldValue(g, 'Site Comments'),
  },
  {
    id: 'levelPrimary',
    header: 'Level (Primary)',
    accessorFn: (g) => getFieldValue(g, 'Level (Primary)'),
  },
  {
    id: 'levelRedundant',
    header: 'Level (Redundant)',
    accessorFn: (g) => getFieldValue(g, 'Level (Redundant)'),
  },
  {
    id: 'depthPrimary',
    header: 'Depth (Primary)',
    accessorFn: (g) => getFieldValue(g, 'Depth (Primary)'),
  },
  {
    id: 'depthRedundant',
    header: 'Depth (Redundant)',
    accessorFn: (g) => getFieldValue(g, 'Depth (Redundant)'),
  },
  {
    id: 'installComments',
    header: 'Install Comments',
    accessorFn: (g) => getFieldValue(g, 'Install Comments'),
  },
];

const visiblePotholeColumns = new Set([
  'select',
  'ph#',
  'date',
  'utility',
  'holeDepth',
  'depthToTop',
  'depthToBottom',
  'material',
  'priority',
  'createdBy',
]);

const visibleManholeColumns = new Set(['select', 'mh#', 'date', 'depth', 'velocity']);

export const potholeColumnVisibilityState: VisibilityState = Object.fromEntries(
  potholeColumns
    .map((c) => c.id)
    .filter((id): id is string => id !== undefined)
    .map((id) => [id, visiblePotholeColumns.has(id)]),
);

export const manholeColumnVisibilityState: VisibilityState = Object.fromEntries(
  manholeColumns
    .map((c) => c.id)
    .filter((id): id is string => id !== undefined)
    .map((id) => [id, visibleManholeColumns.has(id)]),
);
